<template>
  <span>
    <v-tooltip bottom nudge-bottom="7">
      <template #activator="{ attrs, on }">
        <template v-if="complimentChip.color">
          <AppChip
            :color="complimentChip.color"
            :text-color="complimentChip.textColor"
            :custom-style="complimentChip.customStyle"
            v-bind="[attrs, $attrs, chipProps]"
            v-on="on"
          >
            <slot>
              {{ $t(complimentChip.textKey, complimentChip.textDefault) }}
            </slot>
          </AppChip>
        </template>
        <span
          v-else
          v-bind="[attrs, $attrs, chiplesProps]"
          class="secondary--text"
          v-on="on"
        >
          <slot>
            {{ $t(complimentChip.textKey, complimentChip.textDefault) }}
          </slot>
        </span>
      </template>
      <span>
        {{ $t(complimentChip.tooltip, complimentChip.tooltipDefault) }}
      </span>
    </v-tooltip>
  </span>
</template>
<script>
import { defineComponent, toRef, computed } from "vue";
import gql from "fraql";

import AppChip from "@elements/AppChip.vue";

export default defineComponent({
  name: "AppShowEntryClassChip",
  components: { AppChip },
  props: {
    entry: {
      type: Object,
      required: true
    },
    chipProps: {
      type: Object,
      default: () => ({})
    },
    chiplesProps: {
      type: Object,
      default: () => ({})
    }
  },
  setup(props) {
    const entry = toRef(props, "entry");

    const complimentChip = computed(() => {
      const status = entry.value.status;
      const resolution = entry.value.resolution;

      let compliment = {
        tooltip: null,
        tooltipDefault: "",
        customStyle: undefined,
        color: undefined,
        textColor: undefined
      };

      switch (status) {
        case "registered":
          switch (resolution) {
            case "unclear":
              compliment = {
                tooltip: "events:entryCombinedStatus.registered.tooltip",
                tooltipDefault: "draft entry",
                color: undefined,
                textColor: undefined,

                textKey: "events:entryCombinedStatus.registered.badge",
                textDefault: "draft"
              };
              break;
          }
          break;
        case "registration_confirmed":
          switch (resolution) {
            case "unclear":
              compliment = {
                tooltip:
                  "events:entryCombinedStatus.registrationConfirmed.tooltip",
                tooltipDefault: "confirmed entry",
                color: "entrySuccess",
                textColor: "white--text",
                textKey:
                  "events:entryCombinedStatus.registrationConfirmed.badge",
                textDefault: "confirmed"
              };
              break;
            case "candidacy":
              compliment = {
                tooltip: "events:entryCombinedStatus.candidacy.tooltip",
                tooltipDefault: "candidacy",
                color: undefined,
                textColor: undefined,
                textKey: "events:entryCombinedStatus.candidacy.badge",
                textDefault: "candidacy"
              };
              break;
            case "accepted":
              compliment = {
                tooltip: "events:entryCombinedStatus.accepted.tooltip",
                tooltipDefault: "accepted entry",
                color: "entrySuccess",
                textColor: "white--text",
                textKey: "events:entryCombinedStatus.accepted.badge",
                textDefault: "accepted"
              };
              break;
            case "participated":
              compliment = {
                tooltip: "events:entryCombinedStatus.participated.tooltip",
                tooltipDefault: "participated",
                color: "entrySuccess",
                textColor: "white--text",
                textKey: "events:entryCombinedStatus.participated.badge",
                textDefault: "participated"
              };
              break;
            case "rejected":
              compliment = {
                tooltip: "events:entryCombinedStatus.rejected.tooltip",
                tooltipDefault: "rejected entry",
                color: "entryReject",
                textColor: "white--text",
                textKey: "events:entryCombinedStatus.rejected.badge",
                textDefault: "rejected"
              };
              break;
            case "candidacy_rejected": {
              compliment = {
                tooltip: "events:entryCombinedStatus.candidacyRejected.tooltip",
                tooltipDefault: "candidacy rejected",
                color: "entryReject",
                textColor: "white--text",
                textKey: "events:entryCombinedStatus.candidacyRejected.badge",
                textDefault: "candidacy rejected"
              };
              break;
            }

            case "withdrawn":
            case "withdrawn_early":
            case "withdrawn_no_fees":
            case "unpaid":
              compliment = {
                tooltip: "events:entryCombinedStatus.withdrawn.tooltip",
                tooltipDefault: "withdrawn entry",
                color: "entryCancelled",
                textColor: "white--text",
                textKey: "events:entryCombinedStatus.withdrawn.badge",
                textDefault: "withdrawn"
              };
              break;
            case "changed":
              compliment = {
                tooltip: "events:entryCombinedStatus.changed.tooltip",
                tooltipDefault: "changed or substituted entry",
                customStyle: "text-decoration: line-through",
                color: "entryCancelled",
                textColor: "white--text",
                textKey: "events:entryCombinedStatus.changed.badge",
                textDefault: "changed"
              };
              break;
            case "waiting":
              compliment = {
                tooltip: "events:entryCombinedStatus.waiting.tooltip",
                tooltipDefault: "entry on waiting list",
                color: "entryWaiting",
                textColor: "white--text",
                textKey: "events:entryCombinedStatus.waiting.badge",
                textDefault: "waiting"
              };
              break;

            case "no_show":
              compliment = {
                tooltip: "events:entryCombinedStatus.noshow.tooltip",
                tooltipDefault: "no show entry",
                color: "entryInvalid",
                textColor: "white--text",
                textKey: "events:entryCombinedStatus.noshow.badge",
                textDefault: "no show"
              };
              break;
            case "voided":
              compliment = {
                tooltip: "events:entryCombinedStatus.voided.tooltip",
                tooltipDefault: "voided entry",
                color: "entryInvalid",
                textColor: "white--text",
                textKey: "events:entryCombinedStatus.voided.badge",
                textDefault: "voided"
              };
              break;

            case "reserve":
              compliment = {
                tooltip: "events:entryCombinedStatus.reserve.tooltip",
                tooltipDefault: "entry on reserve list",
                color: "entryReserved",
                textColor: "white--text",
                textKey: "events:entryCombinedStatus.reserve.badge",
                textDefault: "reserve"
              };
              break;
          }
          break;
        case "result_confirmed":
          switch (resolution) {
            case "participated":
              compliment = {
                tooltip: "events:entryCombinedStatus.participated.tooltip",
                tooltipDefault: "participated",
                color: "entrySuccess",
                textColor: "white--text",
                textKey: "events:entryCombinedStatus.participated.badge",
                textDefault: "participated"
              };
              break;

            case "withdrawn":
            case "withdrawn_early":
            case "withdrawn_no_fees":
            case "unpaid":
              compliment = {
                tooltip: "events:entryCombinedStatus.withdrawn.tooltip",
                tooltipDefault: "withdrawn entry",
                color: "entryCancelled",
                textColor: "white--text",
                textKey: "events:entryCombinedStatus.withdrawn.badge",
                textDefault: "withdrawn"
              };
              break;
            case "accepted":
              compliment = {
                tooltip: "events:entryCombinedStatus.accepted.tooltip",
                tooltipDefault: "accepted entry",
                color: "entrySuccess",
                textColor: "white--text",
                textKey: "events:entryCombinedStatus.accepted.badge",
                textDefault: "accepted"
              };
              break;

            case "changed":
              compliment = {
                tooltip: "events:entryCombinedStatus.changed.tooltip",
                tooltipDefault: "changed or substituted entry",
                customStyle: "text-decoration: line-through",
                color: "entryCancelled",
                textColor: "white--text",
                textKey: "events:entryCombinedStatus.changed.badge",
                textDefault: "changed"
              };
              break;
            case "no_show":
              compliment = {
                tooltip: "events:entryCombinedStatus.noshow.tooltip",
                tooltipDefault: "no show entry",
                color: "entryInvalid",
                textColor: "white--text",
                textKey: "events:entryCombinedStatus.noshow.badge",
                textDefault: "no show"
              };
              break;
            case "voided":
              compliment = {
                tooltip: "events:entryCombinedStatus.voided.tooltip",
                tooltipDefault: "voided entry",
                color: "entryInvalid",
                textColor: "white--text",
                textKey: "events:entryCombinedStatus.voided.badge",
                textDefault: "voided"
              };
              break;
          }
          break;
        case "unregistered":
          switch (resolution) {
            case "unclear":
            case "competition_cancelled":
            default:
              compliment = {
                tooltip: "events:entryCombinedStatus.unregistered.tooltip",
                tooltipDefault: "unregistered entry",
                customStyle: "text-decoration: line-through",
                color: "entryCancelled",
                textColor: "white--text",
                textKey: "events:entryCombinedStatus.unregistered.badge",
                textDefault: "unregistered"
              };
              break;
          }
          break;
      }

      return compliment;
    });
    return { complimentChip };
  },
  fragments: {
    entryFields: gql`
      fragment _ on Entry {
        id
        status
        resolution
      }
    `
  }
});
</script>
